import React, { useEffect, useState } from "react";
import { DeviceMobile, X, ShareNetwork, Plus, Download } from "@phosphor-icons/react";

const KEY_DISMISSED = "myb-install-prompt-dismissed";
const KEY_INSTALLED = "myb-install-prompt-installed";

/**
 * Soft "Install this app" onboarding prompt.
 *
 * Behaviour:
 *  - Appears as a slide-in card in the bottom-right after a 1.5 s delay so it
 *    never fights with first-render content for attention.
 *  - On Android / desktop Chrome we capture the `beforeinstallprompt` event
 *    and show a single-tap "Install" CTA that hands off to the OS.
 *  - On iOS Safari (no beforeinstallprompt) we show short manual steps:
 *    Share → Add to Home Screen.
 *  - Hidden permanently once the user dismisses (localStorage marker) or
 *    after a successful install (`appinstalled` event).
 *  - Hidden if the app is already running standalone.
 */
const InstallAppPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState("other"); // ios | android | desktop | other

  useEffect(() => {
    // Skip if already installed (standalone display) or previously dismissed.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;
    if (localStorage.getItem(KEY_INSTALLED) === "1") return;
    if (localStorage.getItem(KEY_DISMISSED) === "1") return;

    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    setPlatform(isIOS ? "ios" : isAndroid ? "android" : "desktop");

    let timer;
    const show = () => { timer = setTimeout(() => setVisible(true), 1500); };

    // beforeinstallprompt — Chrome / Edge / Android. Capture the event so we
    // can trigger the native install prompt later from our CTA.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      show();
    };
    const onInstalled = () => {
      localStorage.setItem(KEY_INSTALLED, "1");
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS / desktop fallback — neither path will fire `beforeinstallprompt`
    // reliably (iOS Safari never does; desktop Chrome only with a service
    // worker registered). Show the manual-instructions card after the same
    // delay so users still discover the install option.
    if (isIOS || !isAndroid) show();

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY_DISMISSED, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") {
      localStorage.setItem(KEY_INSTALLED, "1");
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-app-prompt"
      className="fixed bottom-4 right-4 z-50 max-w-[360px] bg-white border border-myb-200 shadow-2xl rounded-sm p-4 animate-in slide-in-from-bottom-4 duration-300 no-print"
      style={{ boxShadow: "0 12px 40px rgba(13,56,128,0.18)" }}
    >
      <button
        onClick={dismiss}
        data-testid="install-app-dismiss"
        className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-myb-500 p-1"
        aria-label="Dismiss"
      ><X size={14} weight="bold" /></button>

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-myb-500 text-white grid place-items-center shrink-0">
          <DeviceMobile size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0 pr-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold">Install MYB Analytics</div>
          <div className="text-sm text-foreground mt-0.5 font-medium">Get a one-tap shortcut on your home screen</div>

          {platform === "ios" ? (
            <div className="text-xs text-muted-foreground mt-3 leading-relaxed space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">1.</span>
                <span>Tap the</span>
                <ShareNetwork size={14} weight="bold" className="text-myb-500" />
                <span><span className="font-semibold text-foreground">Share</span> button at the bottom</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">2.</span>
                <span>Choose</span>
                <Plus size={14} weight="bold" className="text-myb-500" />
                <span><span className="font-semibold text-foreground">Add to Home Screen</span></span>
              </div>
            </div>
          ) : deferredPrompt ? (
            <div className="mt-3">
              <button
                onClick={install}
                data-testid="install-app-install"
                className="text-xs bg-myb-500 hover:bg-myb-600 text-white px-3 py-1.5 rounded-sm font-semibold inline-flex items-center gap-1.5"
              >
                <Download size={12} weight="bold" />
                Install app
              </button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-3 leading-relaxed">
              In Chrome, tap the <span className="font-semibold text-foreground">⋮</span> menu and choose
              <span className="font-semibold text-foreground"> Install app</span> (or <span className="font-semibold text-foreground">Add to Home Screen</span>).
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
